import { useLocation } from "react-router-dom";
import { safeReturnTo } from "@mycompbox/shared";
import { useGitHubStatus, useProjectRepository } from "@/hooks/queries/use-github";
import { ConnectGitHubGate } from "./connect-github-gate";

/**
 * A persistent "Finish setup" prompt for organizations that skipped GitHub in
 * onboarding. Shown while the server has GitHub configured and the organization
 * has no usable (non-suspended) installation; there is no dismiss, it goes away once GitHub is connected.
 * Hidden while loading and on a status error so the page still renders.
 */
export function FinishGitHubSetup({ teamId }: { teamId?: string }) {
  const location = useLocation();
  const returnTo = safeReturnTo(`${location.pathname}${location.search}`) ?? "/";
  const { data: status, isError } = useGitHubStatus(returnTo);

  if (isError || !status || !status.configured || status.installations.some((i) => !i.suspended)) return null;

  return (
    <section
      aria-label="Finish setup: connect GitHub"
      className="space-y-2 rounded-xl border border-amber-500/30 bg-amber-500/[0.04] p-4"
    >
      <div>
        <h2 className="text-sm font-semibold">Finish setup: connect GitHub</h2>
        <p className="text-sm text-muted-foreground">Projects need GitHub for pull requests and deploys.</p>
      </div>
      <ConnectGitHubGate returnTo={returnTo} {...(teamId ? { teamId } : {})} />
    </section>
  );
}

/**
 * The prompt on a project page: only for a URL-only project (a repository URL
 * with no GitHub link), and with the project so non-admins can request access.
 */
export function ProjectFinishGitHubSetup({ teamId }: { teamId: string }) {
  const { data: repo } = useProjectRepository(teamId);
  if (!repo?.gitRemote || repo.github) return null;
  return <FinishGitHubSetup teamId={teamId} />;
}
